import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { LinkIcon, PlusIcon } from "@heroicons/react/24/outline";
import { linksService } from "../../services/api";
import toast from "react-hot-toast";

function CreateLinkPage() {
  const navigate = useNavigate();
  const [isCreating, setIsCreating] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const onSubmit = async (data) => {
    try {
      setIsCreating(true);

      const linkData = {
        originalUrl: data.originalUrl,
        customCode: data.customCode || undefined,
        title: data.title || undefined,
        expiresAt: data.expiresAt || undefined,
      };

      const response = await linksService.create(linkData);

      if (response.data.success) {
        toast.success("Link başarıyla oluşturuldu!");
        navigate("/dashboard/links");
      }
    } catch (error) {
      // Error handling is done in API service
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Yeni Link Oluştur</h1>
        <p className="text-gray-600">
          Uzun URL'nizi kısaltın ve para kazanmaya başlayın
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Form */}
        <div className="lg:col-span-2">
          <form onSubmit={handleSubmit(onSubmit)} className="card">
            <div className="card-header">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                <LinkIcon className="w-5 h-5 mr-2" />
                Link Bilgileri
              </h2>
            </div>
            <div className="card-body space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Kısaltılacak URL <span className="text-error-500">*</span>
                </label>
                <input
                  type="url"
                  className={`form-input ${errors.originalUrl ? "error" : ""}`}
                  placeholder="https://example.com/very/long/url/here"
                  {...register("originalUrl", {
                    required: "URL gereklidir",
                    pattern: {
                      value: /^https?:\/\/.+/,
                      message:
                        "Geçerli bir URL girin (http:// veya https:// ile başlamalı)",
                    },
                  })}
                />
                {errors.originalUrl && (
                  <p className="mt-1 text-sm text-error-600">
                    {errors.originalUrl.message}
                  </p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  Kısaltmak istediğiniz URL'i girin
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Özel Kod (Opsiyonel)
                </label>
                <input
                  type="text"
                  className={`form-input ${errors.customCode ? "error" : ""}`}
                  placeholder="ornek-kod"
                  {...register("customCode", {
                    pattern: {
                      value: /^[a-zA-Z0-9_-]+$/,
                      message:
                        "Sadece harf, rakam, tire ve alt çizgi kullanabilirsiniz",
                    },
                    minLength: {
                      value: 3,
                      message: "En az 3 karakter olmalıdır",
                    },
                    maxLength: {
                      value: 20,
                      message: "En fazla 20 karakter olabilir",
                    },
                  })}
                />
                {errors.customCode && (
                  <p className="mt-1 text-sm text-error-600">
                    {errors.customCode.message}
                  </p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  Boş bırakırsanız otomatik kod oluşturulur
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Başlık (Opsiyonel)
                </label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Link başlığı"
                  {...register("title", {
                    maxLength: {
                      value: 255,
                      message: "En fazla 255 karakter olabilir",
                    },
                  })}
                />
                <p className="mt-1 text-xs text-gray-500">
                  Boş bırakırsanız sayfa başlığı otomatik çekilir
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Son Kullanma Tarihi (Opsiyonel)
                </label>
                <input
                  type="datetime-local"
                  className="form-input"
                  {...register("expiresAt", {
                    validate: (value) => {
                      if (value && new Date(value) <= new Date()) {
                        return "Son kullanma tarihi gelecekte olmalıdır";
                      }
                      return true;
                    },
                  })}
                />
                {errors.expiresAt && (
                  <p className="mt-1 text-sm text-error-600">
                    {errors.expiresAt.message}
                  </p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  Belirtilen tarihten sonra link çalışmayacaktır
                </p>
              </div>

              <div className="flex justify-end space-x-4 pt-6 border-t">
                <button
                  type="button"
                  onClick={() => navigate("/dashboard/links")}
                  className="btn btn-secondary"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  disabled={isCreating}
                  className="btn btn-primary"
                >
                  {isCreating ? (
                    <>
                      <div className="loading-spinner w-4 h-4 mr-2"></div>
                      Oluşturuluyor...
                    </>
                  ) : (
                    <>
                      <PlusIcon className="w-4 h-4 mr-2" />
                      Link Oluştur
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>

        {/* Info Sidebar */}
        <div className="space-y-6">
          <div className="card bg-blue-50 border-blue-200">
            <div className="card-body">
              <h3 className="font-semibold text-blue-900 mb-3">💡 İpuçları</h3>
              <ul className="text-sm text-blue-800 space-y-2">
                <li>• Kısa ve akılda kalıcı kodlar seçin</li>
                <li>• Kaliteli içerikleri linkleyin</li>
                <li>• Sosyal medyada paylaşın</li>
                <li>• Analytics'i takip edin</li>
              </ul>
            </div>
          </div>

          <div className="card bg-green-50 border-green-200">
            <div className="card-body">
              <h3 className="font-semibold text-green-900 mb-3">
                💰 Kazanç Bilgisi
              </h3>
              <ul className="text-sm text-green-800 space-y-2">
                <li>• Her tık ile para kazanın</li>
                <li>• Ülke bazlı CPM oranları</li>
                <li>• Minimum çekim: $10</li>
                <li>• PayPal ile hızlı ödeme</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CreateLinkPage;
